import {
  MixpanelEvent,
  NotificationVariants,
  Text,
  Tooltip,
  useGlobalToasts,
  useMarketAccountsContext,
  useMarketsContext,
  useMixpanel,
  useSecuritiesContext,
  useUserContext,
  VStack,
  type Column,
  type Security,
} from '@talos/kyoko';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import { getRestFailureUserMessage } from '../../../utils/rtkQuery/restFailureError';
import {
  shouldUpdateMultilegLegMarketAccounts,
  updateMultilegLegMarkets,
  useMultilegLegMarketAccountsEditing,
} from '../../MarketDataCard/multilegLegMarketAccounts';
import { useMarketSelectorPanelSaveControls } from '../../MarketSelector/MarketSelectorPanelSaveControls';
import { MultilegQuickMarketSelectorPanel } from '../../MultiLegSummary/MultilegQuickMarketSelectorPanel';
import { getMultilegLegMarketDetails } from '../hooks/actions/common/getMultilegLegMarketDetails';
import type { PriceGridEntity } from '../PriceGridEntity';
import {
  getLegIndexFromPriceGridMarketDataColumnField,
  getPriceGridMarketDataColumnId,
  PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
  PRICE_GRID_MARKET_DATA_LEG_MARKET_SELECTOR_COLUMN_SUFFIX,
  type PriceGridMarketDataColumnRow,
  type PriceGridMarketDataRendererParams,
} from './priceGridMarketDataColumnUtils';
import type { PriceGridSecurityMarketDataColumnParams } from './types';

const PRICE_GRID_LEG_MARKET_SELECTOR_COLUMN_INITIAL_WIDTH = 120;
const MAX_VISIBLE_LEG_MARKETS = 2;
const ALL_MARKETS_LABEL = 'All Markets';
export const PRICE_GRID_LEG_MARKET_SELECTOR_CELL_CLASS = 'price-grid-leg-market-selector-cell';

const LegMarketSelectorCell = styled.button<{ $interactive: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.backgroundInputReadOnly};
  border: 1px solid ${({ theme }) => theme.borderColorInputReadOnly};
  border-radius: ${({ theme }) => theme.borderRadiusSmall}px;
  box-sizing: border-box;
  column-gap: ${({ theme }) => theme.spacing4}px;
  color: ${({ theme }) => theme.colorTextSubtle};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  height: 20px;
  max-width: 100%;
  overflow: hidden;
  padding: 0 ${({ theme }) => theme.spacing4}px;
  width: 100%;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const LegMarketSelectorNames = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LegMarketSelectorRemainingCount = styled.span`
  white-space: nowrap;
`;

function getDisplayName(selection: string, displayNamesByName: ReadonlyMap<string, string | undefined>) {
  return displayNamesByName.get(selection) ?? selection;
}

function getSortedLabels(selections: string[], displayNamesByName: ReadonlyMap<string, string | undefined>) {
  return selections
    .map(selection => ({
      label: getDisplayName(selection, displayNamesByName),
      name: selection,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label) || a.name.localeCompare(b.name))
    .map(selection => selection.label);
}

function getCompactLabel(labels: string[]) {
  const visibleLabels = labels.slice(0, MAX_VISIBLE_LEG_MARKETS);
  return visibleLabels.join(', ');
}

export function getLegMarketSelectorLabels({
  security,
  legIndex,
  marketsByName,
  marketAccountsByName,
}: {
  security: Security;
  legIndex: number | undefined;
  marketsByName: ReadonlyMap<string, { DisplayName?: string } | undefined>;
  marketAccountsByName: ReadonlyMap<string, { DisplayName?: string } | undefined>;
}) {
  const displayNamesByName = new Map<string, string | undefined>([
    ...[...marketsByName.entries()].map(([name, market]) => [name, market?.DisplayName] as const),
    ...[...marketAccountsByName.entries()].map(([name, marketAccount]) => [name, marketAccount?.DisplayName] as const),
  ]);
  const selections = getMultilegLegMarketDetails({ dataSecurity: security, legIndex })
    .map(market => market.MarketAccount ?? market.Market)
    .filter((selection): selection is string => selection != null && selection.length > 0);
  const labels = selections.length > 0 ? getSortedLabels(selections, displayNamesByName) : [ALL_MARKETS_LABEL];

  return {
    labels,
    compactLabel: getCompactLabel(labels),
    remainingCount: Math.max(0, labels.length - MAX_VISIBLE_LEG_MARKETS),
    tooltipLabels: labels,
  };
}

function PriceGridLegMarketSelectorCell({ symbol, legIndex }: { symbol: string; legIndex: number | undefined }) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useUserContext();
  const mixpanel = useMixpanel();
  const { enableInlineMultilegMarketEditing } = useFeatureFlag();
  const { useNewMarketSelector } = useTradingSettings();
  const security = securitiesBySymbol.get(symbol);
  const panelAnchorRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const { legMarketAccounts, onLegMarketAccountsChange, resetLegMarketAccounts } = useMultilegLegMarketAccountsEditing({
    security,
  });
  const { compactLabel, remainingCount, tooltipLabels } = useMemo(
    () =>
      security
        ? getLegMarketSelectorLabels({ security, legIndex, marketsByName, marketAccountsByName })
        : { compactLabel: '', remainingCount: 0, tooltipLabels: [] },
    [legIndex, marketAccountsByName, marketsByName, security]
  );
  const canEditLegMarkets = useNewMarketSelector && enableInlineMultilegMarketEditing;

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCloseWithoutSaving = useCallback(() => {
    resetLegMarketAccounts();
    handleClose();
  }, [handleClose, resetLegMarketAccounts]);

  const handleSave = useCallback(async () => {
    try {
      setSaveLoading(true);
      const updatedMultilegLegMarkets = await updateMultilegLegMarkets({
        security,
        legMarketAccounts,
        orgApiEndpoint,
      });

      if (updatedMultilegLegMarkets) {
        mixpanel.track(MixpanelEvent.SaveMultilegLegMarkets);
        addToast({
          text: `Symbol ${security?.DisplaySymbol ?? security?.Symbol ?? symbol} updated.`,
          variant: NotificationVariants.Positive,
        });
      }

      handleClose();
    } catch (error) {
      addToast({
        text: getRestFailureUserMessage(error) ?? 'Unable to update multileg leg markets.',
        variant: NotificationVariants.Negative,
      });
    } finally {
      setSaveLoading(false);
    }
  }, [addToast, handleClose, legMarketAccounts, mixpanel, orgApiEndpoint, security, symbol]);

  const hasUnsavedChanges = useMemo(
    () => shouldUpdateMultilegLegMarketAccounts(security, legMarketAccounts),
    [security, legMarketAccounts]
  );

  const { panelBottomSuffix, requestClose } = useMarketSelectorPanelSaveControls({
    saveLoading,
    onSave: handleSave,
    onClose: handleClose,
    onCloseWithoutSaving: handleCloseWithoutSaving,
    hasUnsavedChanges,
  });

  const handleToggleSelector = useCallback(() => {
    if (isOpen) {
      requestClose();
      return;
    }

    mixpanel.track(MixpanelEvent.OpenMultilegLegMarketSelector);
    setIsOpen(true);
  }, [isOpen, mixpanel, requestClose]);

  if (!security || !compactLabel) {
    return null;
  }

  return (
    <>
      <Tooltip
        tooltip={
          <VStack gap="spacing2" alignItems="flex-start">
            {tooltipLabels.map((label, index) => (
              <Text key={`${label}-${index}`}>{label}</Text>
            ))}
          </VStack>
        }
        placement="top-start"
        tooltipTestID="price-grid-leg-market-selector-tooltip"
      >
        <LegMarketSelectorCell
          ref={panelAnchorRef}
          type="button"
          data-testid="price-grid-leg-market-selector"
          data-tour={
            canEditLegMarkets && legIndex === 0 ? 'price-grid-multileg-quick-market-selector-tour-anchor' : undefined
          }
          aria-disabled={!canEditLegMarkets}
          $interactive={canEditLegMarkets}
          onClick={event => {
            event.stopPropagation();
            if (!canEditLegMarkets) {
              return;
            }
            handleToggleSelector();
          }}
        >
          <LegMarketSelectorNames>{compactLabel}</LegMarketSelectorNames>
          {remainingCount > 0 ? (
            <LegMarketSelectorRemainingCount>+{remainingCount}</LegMarketSelectorRemainingCount>
          ) : null}
        </LegMarketSelectorCell>
      </Tooltip>
      {canEditLegMarkets && isOpen ? (
        <MultilegQuickMarketSelectorPanel
          security={security}
          legMarketAccounts={legMarketAccounts}
          onLegMarketAccountsChange={onLegMarketAccountsChange}
          isOpen={isOpen}
          onClose={requestClose}
          panelAnchorRef={panelAnchorRef}
          panelBottomSuffix={panelBottomSuffix}
          initialLegIndex={legIndex ?? 0}
        />
      ) : null}
    </>
  );
}

export function getPriceGridLegMarketSelectorColumn(
  column: Column<PriceGridSecurityMarketDataColumnParams, PriceGridMarketDataColumnRow>
): Column<ColDef<PriceGridEntity, string | undefined>, PriceGridMarketDataColumnRow> {
  const colId = getPriceGridMarketDataColumnId(column, PRICE_GRID_MARKET_DATA_LEG_MARKET_SELECTOR_COLUMN_SUFFIX);

  return {
    type: 'custom',
    id: colId,
    field: column.field,
    title: 'Market Selector',
    initialWidth: PRICE_GRID_LEG_MARKET_SELECTOR_COLUMN_INITIAL_WIDTH,
    sortable: false,
    ...PRICE_GRID_MARKET_DATA_LEAF_COLUMN_PROPS,
    params: {
      colId,
      cellClass: PRICE_GRID_LEG_MARKET_SELECTOR_CELL_CLASS,
      headerName: 'Market Selector',
      valueGetter: ({ data }) => (data?.isParentRow ? data.security.Symbol : undefined),
      cellRenderer: (params: PriceGridMarketDataRendererParams<string | undefined>) => {
        const symbol = params.value;
        if (!symbol) {
          return null;
        }

        return (
          <PriceGridLegMarketSelectorCell
            symbol={symbol}
            legIndex={getLegIndexFromPriceGridMarketDataColumnField(column.field)}
          />
        );
      },
    },
  };
}
